// ===========================================
// An enquiry's history (enquiries plan L7, §5.7, §7)
// ===========================================
// Newest first, a page at a time. APPEND-ONLY for everyone, an ADMIN included: there is no edit
// or delete control on a row, and none may be added (the database refuses both). Staff add to it
// with "+ Call", "+ Note" and "Family replied".

import { Alert, Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { Add as AddIcon, Reply as ReplyIcon } from '@mui/icons-material';
import type { EnquiryActivity, EnquiryClientConfig } from '../../../api/enquiry.api';
import { useEnquiryActivities } from '../../../hooks/useEnquiries';
import { formatInSchoolTz } from '../../../utils/dateUtils';
import { ACTIVITY_TYPE_LABELS, dedupeById, formatDay, stageLabel } from './enquiryUi';

const DIRECTION_WORDS: Record<EnquiryActivity['direction'], string | null> = {
  OUTBOUND: 'to the family',
  INBOUND: 'from the family',
  INTERNAL: null,
};
const CONTACT_TYPES = new Set(['PHONE_CALL', 'EMAIL', 'SMS']);

/** `dd/mm/yyyy, h:mm am` in the school's zone. */
function formatWhen(iso: string, timeZone: string): string {
  return formatInSchoolTz(iso, timeZone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function ActivityRow({ activity, config, timeZone }: { activity: EnquiryActivity; config: EnquiryClientConfig | undefined; timeZone: string }) {
  const isMove = !!activity.toStage;
  const direction = CONTACT_TYPES.has(activity.type) ? DIRECTION_WORDS[activity.direction] : null;
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="enquiry-activity">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="caption" fontWeight={600}>
          {ACTIVITY_TYPE_LABELS[activity.type] ?? activity.type}
          {direction ? ` ${direction}` : ''} · {activity.performedByName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatWhen(activity.occurredAt, timeZone)}
        </Typography>
      </Box>
      {isMove && (
        <Typography variant="body2">
          {activity.fromStage
            ? `${stageLabel(config, activity.fromStage)} → ${stageLabel(config, activity.toStage!)}`
            : `Started at ${stageLabel(config, activity.toStage!)}`}
        </Typography>
      )}
      {activity.notes && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {activity.notes}
        </Typography>
      )}
      {(activity.nextAction || activity.ownerName) && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {activity.nextAction && (
            <>
              Next: {activity.nextAction}
              {activity.nextActionDueDate ? ` (due ${formatDay(activity.nextActionDueDate)})` : ''}
            </>
          )}
          {activity.nextAction && activity.ownerName ? ' · ' : ''}
          {activity.ownerName && <>Owner: {activity.ownerName}</>}
        </Typography>
      )}
    </Paper>
  );
}

interface EnquiryHistoryProps {
  enquiryId: string;
  config: EnquiryClientConfig | undefined;
  timeZone: string;
  /** Null hides the buttons (no `enquiries.write`, or a to-review enquiry). */
  onAdd: ((what: 'CALL' | 'NOTE' | 'REPLIED') => void) | null;
}

export default function EnquiryHistory({ enquiryId, config, timeZone, onAdd }: EnquiryHistoryProps) {
  const { data: pages, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useEnquiryActivities(enquiryId);
  // Offset paging over a newest-first list: a colleague's entry added between two pages shifts the
  // second, so one row can arrive twice. Keep the first of each id.
  const activities = dedupeById((pages?.pages ?? []).flatMap((page) => (Array.isArray(page.data) ? page.data : [])));
  const total = pages?.pages[0]?.total ?? activities.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          History
        </Typography>
        {onAdd && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Button size="small" startIcon={<AddIcon />} onClick={() => onAdd('CALL')}>
              Call
            </Button>
            <Button size="small" startIcon={<AddIcon />} onClick={() => onAdd('NOTE')}>
              Note
            </Button>
            <Button size="small" startIcon={<ReplyIcon />} onClick={() => onAdd('REPLIED')}>
              Family replied
            </Button>
          </Stack>
        )}
      </Box>
      {isLoading && (
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={56} />
          <Skeleton variant="rectangular" height={56} />
        </Stack>
      )}
      {isError && <Alert severity="error">Failed to load the history.</Alert>}
      {!isLoading && !isError && activities.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nothing has happened on this enquiry yet.
        </Typography>
      )}
      {!isLoading && !isError && activities.length > 0 && (
        <Stack spacing={1}>
          {activities.map((a) => (
            <ActivityRow key={a.id} activity={a} config={config} timeZone={timeZone} />
          ))}
          {total > activities.length || hasNextPage ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {activities.length} of {total}
              </Typography>
              {hasNextPage && (
                <Button size="small" onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                  {isFetchingNextPage ? 'Loading...' : 'Show older'}
                </Button>
              )}
            </Box>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
